import type {
    ProductCustomization,
    ProductOption,
    PurchasableItem
} from 'shared/store/ecommerce/order'
import type { CacGame, ClickAndCollectPromotion } from 'shared/types'
import { catalogType, promotionOriginType, isDefined } from 'shared/utils'
import { categoryOperationMarketing } from 'shared/utils/ecommerce/const'
import _ from 'lodash'
import type {
    Ingredient,
    Menu,
    Option,
    Product,
    Step
} from 'shared/store/ecommerce/catalog'
import { calculateFinalPriceWithPromotion } from './price'
import type { PromotionOrigin } from 'shared/store/ecommerce/order'
import { menuSize } from '../../const'

const { MENU, PRODUCT, PRODUCT_GROUP, GAME } = catalogType
const {
    S: MENU_SIZE_S,
    M: MENU_SIZE_M,
    L: MENU_SIZE_L,
    XL: MENU_SIZE_XL
} = menuSize

const updateProductSelected = (
    products: any,
    idProduct: string,
    isSelected: boolean
) => {
    return products.map(product => {
        return {
            ...product,
            isSelected: isSelected && product && product.id === idProduct
        }
    })
}

const updateOptionSelected = (
    options: Array<any>,
    idOption?: string,
    isSelected?: boolean
): Array<any> => {
    if (idOption && isSelected) {
        return options.map(option => {
            return {
                ...option,
                isSelected: isSelected && option && option.id === idOption
            }
        })
    } else {
        return options.map(option => {
            return { ...option, isSelected: option.quantityInit === 1 }
        })
    }
}

const updateOptionsDisplayed = (
    products?: Array<?Product>,
    idProduct: string,
    isOption: boolean
) => {
    if (products) {
        const selectedProduct = products.find(
            product => product && product.id === idProduct
        )
        if (selectedProduct) {
            return isOption
                ? selectedProduct.options
                : selectedProduct.ingredients
        }
    }
}

const updateIceOption = (products?: Array<?Product>, idProduct: string) => {
    if (products) {
        const selectedProduct = products.find(
            product => product && product.id === idProduct
        )
        if (selectedProduct) {
            return !!selectedProduct.hasIce
        }
    }
}

const updatePickUpLaterOption = (
    products?: Array<?Product>,
    idProduct: string
) => {
    if (products) {
        const selectedProduct = products.find(
            product => product && product.id === idProduct
        )
        if (selectedProduct) {
            return !!selectedProduct.hasPickUpLater
        }
    }
}

const purchasable = (
    item?: Menu | Product,
    idProductGroup?: string,
    promotion?: ClickAndCollectPromotion,
    promotionOrigin?: PromotionOrigin
): ?PurchasableItem => {
    if (!item) {
        return undefined
    }
    const {
        available,
        categoryId,
        categoryName,
        earnPoints,
        id,
        maxQuantity,
        name,
        price,
        priceS,
        type,
        noIce,
        pickUpLater,
        airshipTagInApp
    } = item || {}

    const basePrice = priceS || price
    const finalPrice = promotion
        ? calculateFinalPriceWithPromotion(basePrice || 0, promotion)
        : basePrice

    let purchasableItem = {
        id,
        label: name,
        quantity: 1,
        originalPrice: basePrice || 0,
        finalPrice: finalPrice || 0,
        categoryId,
        categoryName,
        type: idProductGroup ? PRODUCT_GROUP : type,
        available,
        earnPoints,
        maxQuantity,
        airshipTagInApp
    }

    if (type === MENU) {
        // $FlowFixMe
        const { steps } = item
        purchasableItem = {
            ...purchasableItem,
            canBeCustomized: true,
            menuSize: item.menuSize || MENU_SIZE_M,
            steps: createInitialStepsMenu(steps, item.menuSize)
        }
    }

    if (type === PRODUCT || type === PRODUCT_GROUP) {
        // $FlowFixMe
        const { ingredients, menuExtraPriceTTC, options, hasIce } = item
        const { configurable, composable } = item
        purchasableItem = {
            ...purchasableItem,
            canBeCustomized:
                composable ||
                (options && options.length > 0) ||
                (configurable && ingredients && ingredients.length > 0) ||
                !!hasIce,
            customizedIngredients: [],
            menuExtraPriceTTC,
            options: []
        }
    }

    if (promotion) {
        purchasableItem = {
            ...purchasableItem,
            promotions: [promotion],
            promotionOrigin
        }
    }
    if (idProductGroup) {
        purchasableItem = {
            ...purchasableItem,
            idProductGroup
        }
    }
    if (isDefined(noIce)) {
        purchasableItem = {
            ...purchasableItem,
            noIce
        }
    }
    if (isDefined(pickUpLater)) {
        purchasableItem = {
            ...purchasableItem,
            pickUpLater
        }
    }

    return purchasableItem
}

const updateOrderProductIngredient = (
    order: ?PurchasableItem,
    id: string,
    quantity: number,
    quantityInit: number,
    name: string,
    initialId?: string,
    price: number,
    ingredients?: Array<any>
): ?PurchasableItem => {
    const newIngredient = {
        initial: initialId,
        selected: id,
        initialQuantity: quantityInit,
        quantity,
        label: name,
        price
    }

    if (order && order.customizedIngredients) {
        const customizedIngredients: Array<ProductCustomization> =
            order.customizedIngredients || []

        const indexCategoryAlreadySelected = customizedIngredients.findIndex(
            category => category.selected === id
        )

        if (indexCategoryAlreadySelected > -1) {
            let newCategory
            if (quantity === quantityInit) {
                newCategory = customizedIngredients.filter(
                    category => category.selected !== id
                )
            } else {
                newCategory = [...customizedIngredients]
                newCategory[indexCategoryAlreadySelected] = newIngredient
            }
            //$FlowFixMe
            return {
                ...order,
                customizedIngredients: newCategory
            }
        } else {
            return {
                ...order,
                customizedIngredients: customizedIngredients.concat(
                    newIngredient
                )
            }
        }
    } else {
        return {
            ...order,
            customizedIngredients: [newIngredient]
        }
    }
}

const updateOrderProductIngredientByGroup = (
    order: ?PurchasableItem,
    ingredients: Array<any>
) => {
    return {
        ...order,
        customizedIngredients: ingredients
    }
}

const modifyOptionQuantity = (
    options: Array<ProductOption>,
    index?: number,
    quantityToAdd: number
) => {
    return options.map(option => {
        if (option && option.fromChoice === index) {
            return { ...option, quantity: option.quantity + quantityToAdd }
        } else {
            return option
        }
    })
}

const updateOrderProductOption = (
    order: ?PurchasableItem,
    id?: string,
    quantity?: number,
    quantityInit?: number,
    label?: string,
    index?: number,
    idToRemove?: string
): ?PurchasableItem => {
    const newOption = {
        id: id || '',
        quantity: quantity || 0,
        label: label || '',
        fromChoice: index
    }
    if (order && order.options && order.options.length > 0) {
        let options: Array<ProductOption> = order.options
        if (isDefined(index)) {
            if (idToRemove) {
                options = modifyOptionQuantity(options, index, -1)
                options = options.filter(
                    option => option && option.quantity > 0
                )
            }
            const optionIndex = options.findIndex(
                option => option && option.fromChoice === index
            )
            if (optionIndex > 0) {
                options = modifyOptionQuantity(options, index, 1)
                return {
                    ...order,
                    options
                }
            } else {
                return {
                    ...order,
                    options: options.concat(newOption)
                }
            }
        } else {
            options = [{ ...options[0], quantity }]
            return {
                ...order,
                options: options.filter(option => option && option.quantity > 0)
            }
        }
    } else {
        return {
            ...order,
            options: [newOption]
        }
    }
}

const updateOrderSuggestions = (
    suggestions: Array<PurchasableItem>,
    id: string,
    suggestion: Product,
    quantity: number
) => {
    let newSuggestions: Array<PurchasableItem>
    const suggestionAlreadySelected =
        suggestions && suggestions.find(e => e && e.id === id)

    if (!!suggestionAlreadySelected) {
        const index = suggestions.indexOf(suggestionAlreadySelected)
        if (quantity === 0) {
            newSuggestions = suggestions.filter(s => s && s.id !== id)
        } else {
            newSuggestions = suggestions
            newSuggestions[index] = {
                ...suggestions[index],
                quantity
            }
        }
    } else {
        const purchasableProduct = purchasable(suggestion)
        if (purchasableProduct) {
            newSuggestions = suggestions
                ? suggestions.concat(purchasableProduct)
                : [purchasableProduct]
        }
    }

    return newSuggestions
}

const extractProductsFromSize = (menu: Step, size?: string) => {
    const { products, productsL, productsXL, productsS } = menu
    switch (size) {
        case MENU_SIZE_S:
            return productsS
        case MENU_SIZE_L:
            return productsL
        case MENU_SIZE_XL:
            return productsXL
        default:
            return products
    }
}

const createInitialStepsMenu = (steps: ?Array<Step>, menuSize?: string) => {
    let stepOrder = []
    if (steps && steps.length > 0) {
        for (let i = 0; i < steps.length; i++) {
            //$FlowFixMe
            stepOrder[i] = {}
            const listProducts = extractProductsFromSize(steps[i], menuSize)

            if (listProducts && listProducts.length === 1 && listProducts[0]) {
                const {
                    id,
                    name,
                    categoryId,
                    categoryName,
                    price
                } = listProducts[0]
                stepOrder[i] = {
                    id,
                    label: name,
                    originalPrice: price || 0,
                    finalPrice: price || 0,
                    categoryId,
                    categoryName,
                    quantity: 1,
                    customizedIngredients: [],
                    options: [],
                    type: 'PRODUCT'
                }
            }
        }
    }
    return stepOrder
}

const extractMenuPriceFromSize = (menu: Menu, size: string) => {
    const { price, priceL, priceXL, priceS } = menu
    switch (size) {
        case MENU_SIZE_S:
            return priceS
        case MENU_SIZE_L:
            return priceL
        case MENU_SIZE_XL:
            return priceXL
        default:
            return price
    }
}

const updateOrderMenu = (
    menu: ?Menu,
    size: string,
    order: ?PurchasableItem,
    promotion?: ClickAndCollectPromotion
): ?PurchasableItem => {
    if (menu && order) {
        const currentPrice = extractMenuPriceFromSize(menu, size)
        const finalPrice = promotion
            ? calculateFinalPriceWithPromotion(currentPrice || 0, promotion)
            : currentPrice

        //$FlowFixMe
        return {
            ...order,
            menuSize: size || MENU_SIZE_M,
            originalPrice: currentPrice || 0,
            finalPrice: finalPrice || 0,
            steps:
                order.menuSize !== size
                    ? createInitialStepsMenu(menu.steps, order.menuSize)
                    : order.steps
        }
    }
}

const findIdOptionSelected = (listOptions?: Array<any>) => {
    if (listOptions) {
        const option = listOptions.find(option => option.isSelected === true)
        if (option) {
            return option.id
        }
    }
}

const updateQuantityInitOption = (
    listOptions?: Array<Array<Option>>,
    order: ?PurchasableItem
): ?Array<Array<Option>> => {
    if (!order || !order.options) {
        return listOptions || []
    } else if (listOptions) {
        return listOptions.map((arrayOption, index) =>
            arrayOption.map(option => {
                if (order && order.options) {
                    const optionSelected = order.options.find(
                        orderOption =>
                            orderOption &&
                            option &&
                            orderOption.id === option.id &&
                            ((orderOption.quantity === 1 &&
                                index === orderOption.fromChoice) ||
                                orderOption.quantity > 1)
                    )
                    return {
                        ...option,
                        quantityInit: optionSelected ? 1 : 0
                    }
                } else {
                    return option
                }
            })
        )
    }
}

const updateQuantityInitIngredient = (
    listProducts?: Array<?Ingredient>,
    order: ?PurchasableItem
): ?Array<Ingredient> => {
    if (listProducts) {
        return listProducts.map(option => {
            let ingredient
            if (order && order.customizedIngredients && option) {
                ingredient = order.customizedIngredients.find(
                    ingredient => ingredient.selected === option.id
                )
            }
            return {
                ...option,
                quantityInit: ingredient && ingredient.quantity
            }
        })
    } else {
        return []
    }
}

const findProductInCart = (
    products: Array<PurchasableItem>,
    product: PurchasableItem
): number =>
    products.findIndex(e => {
        const {
            categoryId: categoryId1,
            categoryName: categoryName1,
            available: available1,
            quantity: quantity1,
            ...e1
        } = e
        const {
            categoryId: categoryId2,
            categoryName: categoryName2,
            available: available2,
            quantity: quantity2,
            ...e2
        } = product

        const { promotions, ...withoutPromo1 } = e1
        const { promotions: promotions2, ...withoutPromo2 } = e2
        return product.type === catalogType.GAME
            ? _.isEqual(withoutPromo1, withoutPromo2)
            : _.isEqual(e1, e2)
    })

const operationGameToPurchasable = (
    operationCode: string,
    couponCode: string,
    game: CacGame,
    gameId?: string
): PurchasableItem => {
    const { id, earnPoints, sysName, price } = game || {}
    return {
        id: (categoryOperationMarketing?.categoryId ?? '') + id,
        gameId,
        type: GAME,
        operationCode,
        label: sysName,
        categoryId: categoryOperationMarketing?.categoryId ?? '',
        categoryName: categoryOperationMarketing?.categoryName ?? '',
        finalPrice: price,
        originalPrice: price,
        quantity: 1,
        promotions: [{ couponCode }],
        promotionOrigin: promotionOriginType.OPERATION_REPEAT_COUPON,
        available: true,
        canBeCustomized: false,
        earnPoints
    }
}

const getCartThresholds = (
    products?: Array<PurchasableItem>,
    promotions?: Array<ClickAndCollectPromotion>
) => {
    const thresholds = []
    promotions &&
        promotions.map(promotion => {
            promotion &&
                promotion.threshold &&
                thresholds.push(promotion.threshold)
        })
    products &&
        products.map(product => {
            product &&
                product.promotions &&
                product.promotions.length > 0 &&
                product.promotions.map(promotion => {
                    promotion &&
                        promotion.threshold &&
                        thresholds.push(promotion.threshold)
                })
        })
    return thresholds
}

const cartPriceIsLowerThanPromotionThreshold = (
    priceTotal: number,
    products?: Array<PurchasableItem>,
    promotions?: Array<ClickAndCollectPromotion>
) => {
    const thresholds = getCartThresholds(products, promotions)
    return {
        cartPriceTooLow: thresholds.find(threshold => threshold > priceTotal),
        thresholds
    }
}

export {
    cartPriceIsLowerThanPromotionThreshold,
    findIdOptionSelected,
    findProductInCart,
    operationGameToPurchasable,
    purchasable,
    updateOptionsDisplayed,
    updateIceOption,
    updatePickUpLaterOption,
    updateProductSelected,
    updateOrderMenu,
    updateQuantityInitIngredient,
    updateOrderProductIngredient,
    updateOrderProductOption,
    updateOptionSelected,
    updateQuantityInitOption,
    updateOrderSuggestions,
    updateOrderProductIngredientByGroup
}
